/**
 * @file エラーコード一元化で、heliport.jsプロジェクトで管理するエラーコード。
 * @see https://hikky.atlassian.net/wiki/spaces/VKETCLOUD/pages/912295181
 */

import {z} from 'zod';

/**
 * heliport.jsプロジェクトで管理するエラーコードで、大カテゴリと中カテゴリと紐づけられる情報の一覧。
 *
 * @remarks
 * 大カテゴリ、および中カテゴリのキーは、英大文字で始まり、それに数字・英字が続く文字列とする必要がある。
 * さらに、パスカルケースとし、頭字語はすべて大文字とする。
 */
export const CategoriesAndSubCategories = {
  Heliodor: {
    code: 'HEL',
    subCategories: {
      ExternalScript: {
        title: 'Heliodor HeliScript extern構文',
        description: 'HeliScriptのextern構文により、呼び出し可能なJavaScript関数を指定できる。'
      }
    }
  },
  RTC: {
    code: 'RTC',
    subCategories: {
      OldSkyway: {
        title: 'RTC 旧Skyway',
        description:
          'HeliodorのTypeScript層において、座標・音声チャンネルとして旧Skywayが選択されているとき。'
      }
    }
  },
  Platform: {
    code: 'PTL',
    subCategories: {
      TypeScript: {
        title: 'PLT TS層',
        description: 'HeliodorのTypeScript層においてのエラー'
      }
    }
  }
} as const satisfies Record<
  string,
  {
    /** 大カテゴリのキーと対応し、エラーコードの接頭辞となる英大文字3文字の文字列。 */
    code: string;
    /**
     * 中カテゴリと紐づけられる情報の一覧。
     */
    subCategories: Record<
      string,
      {
        /**
         * 大カテゴリと中カテゴリの組み合わせと対応するプロジェクト・モジュールなど全体を端的に表す名称。レジストリファイルのメタデータとして使われる。一般公開はされない。
         * @see {@link https://github.com/VRHIKKY/VketCloud_ErrorCode/blob/8061f98813a3e7b92d318e346d9f502b2ac0e206/jsonSchemas/errors.json#L7-L9}
         */
        title: string;
        /**
         * 大カテゴリと中カテゴリの組み合わせと対応するプロジェクト・モジュールなど全体についての説明。レジストリファイルのメタデータとして使われる。一般公開はされない。
         * @see {@link https://github.com/VRHIKKY/VketCloud_ErrorCode/blob/8061f98813a3e7b92d318e346d9f502b2ac0e206/jsonSchemas/errors.json#L10-L12}
         */
        description?: string;
      }
    >;
  }
>;

export type ErrorCodeTemplate = Record<
  keyof typeof CategoriesAndSubCategories,
  Record<
    Extract<
      keyof (typeof CategoriesAndSubCategories)[keyof typeof CategoriesAndSubCategories]['subCategories'],
      string
    >,
    Record<
      string,
      Record<
        string,
        Record<
          string,
          {
            /**
             * **一般公開される**エラーの説明。
             *
             * @remarks
             * エラーコードがどういった場合にどこで発生するか、特定できる情報を記述する。
             */
            title: string;
            /**
             * 関連URL。
             *
             * @remarks
             * ドキュメントや、リポジトリのエラーコードを送出しているコードなどのURL一覧。
             * 一般公開されない情報。
             */
            links?: string[];
          }
        >
      >
    >
  >
>;

/**
 * heliport.jsプロジェクトで管理するエラーコードで、実際に発生し得る、大カテゴリ、中カテゴリ、カテゴリ、操作、理由の組み合わせを階層状に並べた一覧。
 * また、原因の値には、エラーコードについての情報を含める。
 *
 * @remarks
 * **ここにエラーコード (キー) を追加する手順・ルールは、本リポジトリのREADMEの「新規エラーコードを追加する方法」を参照すること。**
 *
 * 各キーは、英大文字で始まり、それに数字・英字が続く文字列とする必要がある。
 * さらに、パスカルケースとし、頭字語はすべて大文字とする。
 *
 * エラーコードのうち、中カテゴリ、カテゴリ、操作、原因はキーのみこの定数で手動管理し、各部分のキーに対応するコード (1〜99) は
 * ビルド時、もしくは `yarn generate-error-codes` を実行することで、自動生成される。
 * その際、キーとコードの対応関係は維持する必要があるため、自動生成されたコードは `scripts/models/errorKeysGeneratedCodePairs.json` へ保持される。
 */
export const ErrorCode = {
  Heliodor: {
    ExternalScript: {
      Other: {
        Other: {
          UncaughtError: {
            title:
              'HeliScriptのextern構文で指定したJavaScript関数を呼び出したとき、同期関数で例外が throw されたとき、もしくは非同期関数で Promise.reject が返ったときのエラー。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/issues/12421',
              'https://github.com/VRHIKKY/WorldViewer/blob/69114008523bd85d0fe94b42a54190755771c759/Heliodor/Source/Independent/Heliv/CHeliVM_ForJS.cpp#L67-L79',
              'https://github.com/VRHIKKY/WorldViewer/blob/69114008523bd85d0fe94b42a54190755771c759/Heliodor/Source/Independent/Heliv/CHeliVM_ForJS.cpp#L1168-L1191'
            ]
          }
        }
      }
    }
  },
  RTC: {
    OldSkyway: {
      Other: {
        Other: {
          Other: {
            title: 'Skywayのerrorイベントで未知のtypeが返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L265-L266'
            ]
          }
        }
      },
      Peer: {
        Event: {
          RoomError: {
            title: 'Skywayのerrorイベントで「room-error」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L197-L202'
            ]
          },
          Permission: {
            title: 'Skywayのerrorイベントで「permission」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L203-L206'
            ]
          },
          ListError: {
            title: 'Skywayのerrorイベントで「list-error」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L207-L210'
            ]
          },
          Disconnected: {
            title: 'Skywayのerrorイベントで「disconnected」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L211-L214'
            ]
          },
          SocketError: {
            title: 'Skywayのerrorイベントで「socket-error」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L216-L219'
            ]
          },
          InvalidId: {
            title: 'Skywayのerrorイベントで「invalid-error」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L221-L224'
            ]
          },
          UnavailableId: {
            title: 'Skywayのerrorイベントで「unavailable-id」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L225-L228'
            ]
          },
          InvalidKey: {
            title: 'Skywayのerrorイベントで「invalid-key」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L229-L232'
            ]
          },
          InvalidDomain: {
            title: 'Skywayのerrorイベントで「invalid-domain」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L233-L236'
            ]
          },
          Authentication: {
            title: 'Skywayのerrorイベントで「authentication」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L237-L240'
            ]
          },
          ServerError: {
            title: 'Skywayのerrorイベントで「server-error」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L241-L244'
            ]
          },
          SFUClientNotSupported: {
            title: 'Skywayのerrorイベントで「sfu-client-not-supported」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L245-L248'
            ]
          },
          PeerUnavailable: {
            title: 'Skywayのerrorイベントで「peer-unavailable」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L250-L253'
            ]
          },
          SignalingLimited: {
            title: 'Skywayのerrorイベントで「signaling-limited」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L255-L264'
            ]
          },
          SFULimited: {
            title: 'Skywayのerrorイベントで「sfu-limited」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L255-L264'
            ]
          },
          TurnLimited: {
            title: 'Skywayのerrorイベントで「turn-limited」が返った。',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/bd9873b98f8b5ca103f84a945f60ec1c32942df2/Heliodor/EmscriptenBuild/src/networklib/positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider.ts#L255-L264'
            ]
          }
        }
      }
    }
  },
  Platform: {
    TypeScript: {
      MediaStream: {
        Audio: {
          Other: {
            title:
              'IHeliNetworkのコネクション前にhel_net_init_mic_deviceやhel_net_change_mic_deviceが呼ばれた場合',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/207b309d6e151cd937589c5cf104beee86020b4f/Heliodor/EmscriptenBuild/src/heliodor_skyway.ts#L381',
              'https://github.com/VRHIKKY/WorldViewer/blob/207b309d6e151cd937589c5cf104beee86020b4f/Heliodor/EmscriptenBuild/src/heliodor_skyway.ts#L413'
            ]
          },
          Exception: {
            title:
              'TypeScript / MediaStream / Audio / 例外エラーが発生 (マイク無許可時・マイクデバイスが一つも存在しないとき)',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/207b309d6e151cd937589c5cf104beee86020b4f/Heliodor/EmscriptenBuild/src/networklib/CHeliNetwork.ts#L1379'
            ]
          },
          MicDeviceException: {
            title:
              'TypeScript / MediaStream / Audio / 例外エラーが発生（マイクデバイス切り替え時）',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/207b309d6e151cd937589c5cf104beee86020b4f/Heliodor/EmscriptenBuild/src/networklib/CHeliNetwork.ts#L1417'
            ]
          },
          MicDeviceChange: {
            title: 'TypeScript / MediaStream / Audio / マイクデバイスの切り替えが出来なかった時',
            links: [
              'https://github.com/VRHIKKY/WorldViewer/blob/207b309d6e151cd937589c5cf104beee86020b4f/Heliodor/EmscriptenBuild/src/networklib/CHeliNetwork.ts#L1415'
            ]
          }
        }
      },
      Config: {
        Status: {
          NotFound: {
            title: 'コンフィグファイル(config.js)がなかった',
            links: []
          }
        }
      }
    }
  }
} as const satisfies ErrorCodeTemplate;

const errorKeySchema = z.string().regex(/^[A-Z][0-9A-Za-z]*$/);
export const errorKeysGeneratedCodePairsSchema = z.array(
  z.object({
    keys: z.tuple([errorKeySchema, errorKeySchema]).rest(errorKeySchema),
    code: z.number().min(0).max(99)
  })
);
export type ErrorKeysGeneratedCodePairs = z.infer<typeof errorKeysGeneratedCodePairsSchema>;
